import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Store, Upload, Eye, CreditCard, Users, Lock, MapPin, Navigation } from 'lucide-react';
import { authAPI } from '../services/api';
import { toast } from '../hooks/use-toast';

const StoreConfig = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [formData, setFormData] = useState({
    store_name: '',
    store_logo: '',
    address: '',
    description: '',
    location: null // GeoJSON Point
  });
  const [paymentConfig, setPaymentConfig] = useState({
    method: 'p2p',
    bank_name: '',
    account_type: '',
    account_number: '',
    account_holder: '',
    phone_number: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');

  useEffect(() => {
    if (user && user.store_config) {
      setFormData({
        store_name: user.store_config.store_name || '',
        store_logo: user.store_config.store_logo || '',
        address: user.store_config.address || '',
        description: user.store_config.description || '',
        location: user.store_config.location || null
      });
      setLogoPreview(user.store_config.store_logo || '');
      
      // Load payment config if exists
      if (user.store_config.payment_config) {
        setPaymentConfig(user.store_config.payment_config);
      }
    }
  }, [user]);

  // Get current location
  const getLocation = () => {
    if (!navigator.geolocation) {
      toast({ title: 'Error', description: 'Tu navegador no soporta geolocalización', variant: 'destructive' });
      return;
    }

    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = {
          type: 'Point',
          coordinates: [position.coords.longitude, position.coords.latitude]
        };
        setFormData(prev => ({ ...prev, location }));
        toast({ title: '¡Ubicación obtenida!', description: 'Tu ubicación se ha guardado correctamente' });
        setGettingLocation(false);
      },
      (error) => {
        console.error('Geolocation error:', error);
        toast({ title: 'Error', description: 'No se pudo obtener tu ubicación', variant: 'destructive' });
        setGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const isNewStore = !user?.store_config;

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let logoUrl = formData.store_logo;

      // Upload logo if new file selected
      if (logoFile) {
        const uploadResult = await authAPI.uploadProfileImage(logoFile);
        logoUrl = `${process.env.REACT_APP_BACKEND_URL}${uploadResult.url}`;
      }

      const configData = {
        ...formData,
        store_logo: logoUrl,
        payment_config: paymentConfig,
        location: formData.location
      };

      const response = await authAPI.updateStoreConfig(configData);
      updateUser(response);

      toast({
        title: isNewStore ? '¡Tienda creada!' : '¡Configuracion guardada!',
        description: isNewStore ? 'Ya eres vendedor en Zona Libre' : 'Tu tienda ha sido actualizada correctamente',
      });

      // Redirect to seller products after configuring store
      setTimeout(() => {
        navigate('/seller/products');
      }, 1000);
    } catch (error) {
      console.error('Error updating store config:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo actualizar la configuración',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
        {isNewStore && (
          <div className="bg-gradient-to-r from-orange-500 to-amber-400 rounded-xl p-6 text-white mb-2">
            <div className="flex items-center gap-3 mb-2">
              <Store className="w-8 h-8" />
              <h1 className="text-2xl font-black">Crea tu Tienda en Zona Libre</h1>
            </div>
            <p className="text-white/80 text-sm">Completa los datos y empieza a vender hoy. Al guardar, tu cuenta se convertira en cuenta de vendedor.</p>
          </div>
        )}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">{isNewStore ? 'Nueva Tienda' : 'Configuracion de Tienda'}</h1>
            <p className="text-gray-600 mt-1">{isNewStore ? 'Llena los datos de tu nueva tienda' : 'Personaliza como los compradores veran tu tienda'}</p>
          </div>
          <Button
            onClick={() => setShowPreview(true)}
            variant="outline"
            className="flex items-center"
          >
            <Eye className="w-4 h-4 mr-2" />
            Vista Previa
          </Button>
        </div>

        <Card className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Store Name */}
            <div>
              <Label htmlFor="store_name">Nombre de la Tienda *</Label>
              <Input
                id="store_name"
                value={formData.store_name}
                onChange={(e) => setFormData({ ...formData, store_name: e.target.value })}
                placeholder="Ej: TechStore Oficial"
                required
              />
              <p className="text-sm text-gray-500 mt-1">
                Este nombre aparecerá en todos tus productos
              </p>
            </div>

            {/* Store Logo */}
            <div>
              <Label htmlFor="store_logo">Logo de la Tienda</Label>
              <div className="mt-2 flex items-center space-x-4">
                {logoPreview && (
                  <img
                    src={logoPreview}
                    alt="Logo preview"
                    className="w-24 h-24 object-cover rounded-lg border-2 border-gray-300"
                  />
                )}
                <div className="flex-1">
                  <Input
                    id="store_logo"
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('store_logo').click()}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Subir Logo
                  </Button>
                  <p className="text-sm text-gray-500 mt-2">
                    Imagen cuadrada recomendada (mínimo 200x200px)
                  </p>
                </div>
              </div>
            </div>

            {/* Address */}
            <div>
              <Label htmlFor="address">Dirección</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Ej: Av. Principal 123, Ciudad de México"
              />
            </div>

            {/* Location - For proximity search */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="font-medium text-blue-900">Ubicación para entregas</h3>
                  <p className="text-sm text-blue-700 mt-1">
                    Configura tu ubicación para que los compradores vean el tiempo de entrega estimado.
                  </p>
                  
                  {formData.location ? (
                    <div className="mt-3 flex items-center gap-3">
                      <span className="text-sm text-green-700 bg-green-100 px-2 py-1 rounded">
                        ✓ Ubicación configurada
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={getLocation}
                        disabled={gettingLocation}
                      >
                        {gettingLocation ? 'Obteniendo...' : 'Actualizar'}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      onClick={getLocation}
                      disabled={gettingLocation}
                      className="mt-3 bg-blue-600 hover:bg-blue-700"
                    >
                      <Navigation className="w-4 h-4 mr-2" />
                      {gettingLocation ? 'Obteniendo ubicación...' : 'Obtener mi ubicación'}
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description">Descripción de la Tienda</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe tu tienda, productos que vendes, políticas, etc..."
                rows={4}
              />
            </div>

            {/* Payment Configuration Section */}
            <div className="border-t pt-6">
              <div className="flex items-center mb-4">
                <CreditCard className="w-5 h-5 mr-2 text-zl-orange" />
                <h3 className="text-lg font-semibold">Configuración de Pagos</h3>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-base mb-3 block">¿Cómo deseas recibir los pagos?</Label>
                  <RadioGroup
                    value={paymentConfig.method}
                    onValueChange={(value) => setPaymentConfig({ ...paymentConfig, method: value })}
                    className="space-y-3"
                  >
                    <div className="flex items-start space-x-3 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
                      <RadioGroupItem value="p2p" id="p2p" className="mt-1" />
                      <div className="flex-1">
                        <Label htmlFor="p2p" className="cursor-pointer">
                          <div className="flex items-center">
                            <Users className="w-4 h-4 mr-2 text-green-600" />
                            <span className="font-semibold">P2P (Persona a Persona)</span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            El comprador realiza la compra y tú le envías las instrucciones de pago directamente (transferencia, pago móvil, etc.)
                          </p>
                        </Label>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3 p-4 border rounded-lg bg-gray-50 opacity-60 cursor-not-allowed">
                      <RadioGroupItem value="managed" id="managed" disabled className="mt-1" />
                      <div className="flex-1">
                        <Label htmlFor="managed" className="cursor-not-allowed">
                          <div className="flex items-center">
                            <Lock className="w-4 h-4 mr-2 text-gray-400" />
                            <span className="font-semibold text-gray-500">Gestionado por Zona Libre</span>
                            <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">Próximamente</span>
                          </div>
                          <p className="text-sm text-gray-500 mt-1">
                            Los pagos se procesarán a través de nuestra pasarela segura. Disponible pronto.
                          </p>
                        </Label>
                      </div>
                    </div>
                  </RadioGroup>
                </div>

                {/* P2P Payment Details */}
                {paymentConfig.method === 'p2p' && (
                  <Card className="p-4 bg-green-50 border-green-200">
                    <h4 className="font-semibold text-green-800 mb-4 flex items-center">
                      <CreditCard className="w-4 h-4 mr-2" />
                      Datos para recibir pagos
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="bank_name">Nombre del Banco</Label>
                        <Input
                          id="bank_name"
                          value={paymentConfig.bank_name}
                          onChange={(e) => setPaymentConfig({ ...paymentConfig, bank_name: e.target.value })}
                          placeholder="Ej: Banco Nacional"
                        />
                      </div>
                      <div>
                        <Label htmlFor="account_type">Tipo de Cuenta</Label>
                        <Select
                          value={paymentConfig.account_type}
                          onValueChange={(value) => setPaymentConfig({ ...paymentConfig, account_type: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecciona tipo" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ahorro">Cuenta de Ahorro</SelectItem>
                            <SelectItem value="corriente">Cuenta Corriente</SelectItem>
                            <SelectItem value="pago_movil">Pago Móvil</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="account_number">Número de Cuenta</Label>
                        <Input
                          id="account_number"
                          value={paymentConfig.account_number}
                          onChange={(e) => setPaymentConfig({ ...paymentConfig, account_number: e.target.value })}
                          placeholder="Ej: 0000-0000-0000-0000"
                        />
                      </div>
                      <div>
                        <Label htmlFor="account_holder">Nombre del Titular</Label>
                        <Input
                          id="account_holder"
                          value={paymentConfig.account_holder}
                          onChange={(e) => setPaymentConfig({ ...paymentConfig, account_holder: e.target.value })}
                          placeholder="Nombre completo"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone_number">Número Telefónico</Label>
                        <Input
                          id="phone_number"
                          value={paymentConfig.phone_number}
                          onChange={(e) => setPaymentConfig({ ...paymentConfig, phone_number: e.target.value })}
                          placeholder="Ej: +507 6XXX-XXXX"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-green-700 mt-3">
                      * Esta información se mostrará al comprador cuando realice una compra para que pueda pagarte directamente.
                    </p>
                  </Card>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex space-x-3">
              <Button
                type="submit"
                className="bg-zl-orange hover:bg-zl-orange-600"
                disabled={loading}
              >
                {loading ? 'Guardando...' : 'Guardar Configuración'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/seller/products')}
              >
                Cancelar
              </Button>
            </div>
          </form>
        </Card>

      {/* Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Vista Previa de la Tienda</DialogTitle>
          </DialogHeader>
          <div className="p-6">
            <div className="flex items-center space-x-4 mb-6">
              {(logoPreview || formData.store_logo) && (
                <img
                  src={logoPreview || formData.store_logo}
                  alt="Store logo"
                  className="w-20 h-20 rounded-full object-cover border-2 border-gray-300"
                />
              )}
              <div>
                <h2 className="text-2xl font-bold">{formData.store_name || 'Nombre de Tienda'}</h2>
                {formData.address && (
                  <p className="text-gray-600 text-sm mt-1">📍 {formData.address}</p>
                )}
              </div>
            </div>
            {formData.description && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-2">Descripción</h3>
                <p className="text-gray-700">{formData.description}</p>
              </div>
            )}
            {!formData.store_name && (
              <div className="text-center text-gray-500 py-8">
                <Store className="w-16 h-16 mx-auto mb-2 text-gray-300" />
                <p>Completa el formulario para ver la vista previa</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StoreConfig;
